if (!customElements.get('budetz-style-finder')) {
  class BudetzStyleFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.intro = this.querySelector('[data-bsf-intro]');
      this.quiz = this.querySelector('[data-bsf-quiz]');
      this.result = this.querySelector('[data-bsf-result]');
      this.questions = Array.from(this.querySelectorAll('[data-bsf-question]'));
      this.startButton = this.querySelector('[data-bsf-start]');
      this.backButton = this.querySelector('[data-bsf-back]');
      this.nextButton = this.querySelector('[data-bsf-next]');
      this.restartButton = this.querySelector('[data-bsf-restart]');
      this.progress = this.querySelector('[data-bsf-progress]');
      this.progressBar = this.querySelector('[data-bsf-progress-bar]');
      this.progressName = this.querySelector('[data-bsf-progress-name]');
      this.progressCount = this.querySelector('[data-bsf-progress-count]');
      this.globalStatus = this.querySelector('[data-bsf-global-status]');
      this.emailForm = this.querySelector('.budetz-style-email-form');
      this.emailTags = this.querySelector('[data-bsf-email-tags]');
      this.emailConsent = this.querySelector('[data-bsf-email-consent]');
      this.emailClientError = this.querySelector('[data-bsf-email-client-error]');
      this.emailSuccess = this.querySelector('[data-bsf-email-success]');
      this.emailServerError = this.querySelector('[data-bsf-email-error]');
      this.emailFields = this.querySelector('[data-bsf-email-fields]');
      this.resultGallery = this.querySelector('[data-bsf-result-gallery]');
      this.resultGalleryTrack = this.querySelector('[data-bsf-result-gallery-track]');
      this.isPopup = this.dataset.displayMode === 'popup';
      this.popup = this.querySelector('[data-bsf-popup]');
      this.dialog = this.querySelector('[data-bsf-dialog]');
      this.dialogScroll = this.querySelector('[data-bsf-dialog-scroll]');
      this.popupOpenButton = this.querySelector('[data-bsf-popup-open]');
      this.popupCloseButtons = Array.from(this.querySelectorAll('[data-bsf-popup-close]'));
      this.popupDismissButton = this.querySelector('[data-bsf-popup-dismiss]');
      this.popupIsOpen = false;
      this.lastFocusedElement = null;
      this.autoOpenTimer = null;
      this.dismissStorageKey = 'budetz_style_finder_popup_dismissed_until_v2';

      this.state = {
        step: 0,
        images: [],
        intensity: '',
        occasion: '',
      };
      this.storageKey = `budetzStyleFinder:${this.dataset.sectionId || 'page'}`;
      this.stepNames = ['Your image', 'Stand-out level', 'Occasion'];

      try {
        const configNode = this.querySelector('[data-bsf-config]');
        this.config = JSON.parse(configNode.textContent);
      } catch (error) {
        this.disableWithError('The Style Image Finder could not load. Please refresh the page and try again.');
        return;
      }

      this.startButton?.addEventListener('click', () => this.showQuestion(0));
      this.backButton?.addEventListener('click', () => this.goBack());
      this.nextButton?.addEventListener('click', () => this.goNext());
      this.restartButton?.addEventListener('click', () => this.restart());
      this.addEventListener('change', (event) => this.handleAnswerChange(event));
      this.emailForm?.addEventListener('submit', (event) => this.handleEmailSubmit(event));
      this.popupOpenButton?.addEventListener('click', () => this.openPopup());
      this.popupCloseButtons.forEach((button) => button.addEventListener('click', () => this.closePopup()));
      this.popupDismissButton?.addEventListener('click', () => this.closePopup({ dismiss: true }));

      this.boundDocumentKeydown = (event) => this.handleDocumentKeydown(event);
      this.boundDocumentClick = (event) => this.handleDocumentClick(event);
      if (this.isPopup) {
        document.addEventListener('keydown', this.boundDocumentKeydown);
        document.addEventListener('click', this.boundDocumentClick);
      }

      const restoredAfterEmail = this.restoreAfterEmailPost();
      if (!restoredAfterEmail) {
        this.showIntro(false);
      }
      if (this.isPopup && !restoredAfterEmail) this.scheduleAutoOpen();
    }

    disconnectedCallback() {
      if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer);
      if (this.boundDocumentKeydown) document.removeEventListener('keydown', this.boundDocumentKeydown);
      if (this.boundDocumentClick) document.removeEventListener('click', this.boundDocumentClick);
      if (this.popupIsOpen) {
        document.documentElement.classList.remove('budetz-style-finder-popup-lock');
        document.body.classList.remove('budetz-style-finder-popup-lock');
      }
    }

    disableWithError(message) {
      if (this.startButton) this.startButton.disabled = true;
      this.showGlobalStatus(message, true);
    }

    showIntro(focus = true) {
      this.intro.hidden = false;
      this.quiz.hidden = true;
      this.result.hidden = true;
      this.scrollDialogToTop();
      if (focus) this.startButton?.focus();
    }

    showQuestion(index, focus = true) {
      this.state.step = Math.max(0, Math.min(index, this.questions.length - 1));
      this.intro.hidden = true;
      this.quiz.hidden = false;
      this.result.hidden = true;
      this.clearGlobalStatus();

      this.questions.forEach((question, questionIndex) => {
        question.hidden = questionIndex !== this.state.step;
      });

      const currentQuestion = this.questions[this.state.step];
      const progressValue = this.state.step + 1;
      const isLastStep = progressValue === this.questions.length;
      const width = `${(progressValue / this.questions.length) * 100}%`;

      this.progress.setAttribute('aria-valuenow', String(progressValue));
      this.progressBar.style.width = width;
      this.progressName.textContent = this.stepNames[this.state.step];
      this.progressCount.textContent = `${progressValue} of ${this.questions.length}`;
      this.nextButton.textContent = isLastStep ? 'See My Result' : 'Continue';
      this.updateCurrentQuestionState();
      this.scrollDialogToTop();

      if (focus) {
        window.requestAnimationFrame(() => currentQuestion.querySelector('[data-bsf-step-heading]')?.focus());
      }
    }

    goBack() {
      if (this.state.step === 0) {
        this.showIntro();
        return;
      }
      this.showQuestion(this.state.step - 1);
    }

    goNext() {
      if (!this.currentAnswerIsValid()) {
        this.setQuestionError('Please choose an answer before continuing.');
        return;
      }

      if (this.state.step < this.questions.length - 1) {
        this.showQuestion(this.state.step + 1);
        return;
      }

      this.showResult();
    }

    handleAnswerChange(event) {
      const input = event.target.closest('.budetz-style-option__input');
      if (!input || !this.contains(input)) return;

      const question = input.closest('[data-bsf-question]')?.dataset.bsfQuestion;
      this.clearQuestionError(input.closest('[data-bsf-question]'));

      if (question === 'image') {
        if (input.checked && !this.state.images.includes(input.value)) {
          if (this.state.images.length >= 2) {
            input.checked = false;
            this.setQuestionError('You can choose up to two images.', input.closest('[data-bsf-question]'));
            this.updateImageCount();
            return;
          }
          this.state.images.push(input.value);
        } else if (!input.checked) {
          this.state.images = this.state.images.filter((image) => image !== input.value);
        }
        this.updateImageCount();
      } else if (question === 'intensity') {
        this.state.intensity = input.value;
      } else if (question === 'occasion') {
        this.state.occasion = input.value;
      }

      this.updateCurrentQuestionState();
    }

    updateImageCount() {
      const count = this.querySelector('[data-bsf-image-count]');
      if (!count) return;

      if (this.state.images.length === 0) {
        count.textContent = '0 of 2 selected';
        return;
      }

      const primary = this.config.profiles[this.state.images[0]]?.title;
      const primaryCopy = primary ? ` Main image: ${primary}.` : '';
      count.textContent = `${this.state.images.length} of 2 selected.${primaryCopy}`;
    }

    currentAnswerIsValid() {
      if (this.state.step === 0) return this.state.images.length > 0 && this.state.images.length <= 2;
      if (this.state.step === 1) return Boolean(this.state.intensity);
      return Boolean(this.state.occasion);
    }

    updateCurrentQuestionState() {
      if (this.nextButton) this.nextButton.disabled = !this.currentAnswerIsValid();
    }

    setQuestionError(message, question = this.questions[this.state.step]) {
      const error = question?.querySelector('[data-bsf-question-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    clearQuestionError(question = this.questions[this.state.step]) {
      const error = question?.querySelector('[data-bsf-question-error]');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    showResult({ focus = true } = {}) {
      const primary = this.config.profiles[this.state.images[0]];
      const secondary = this.config.profiles[this.state.images[1]];
      const intensity = this.config.intensities[this.state.intensity];
      const occasion = this.config.occasions[this.state.occasion];

      if (!primary || !intensity || !occasion) {
        this.showQuestion(0);
        this.setQuestionError('Please complete all three questions.');
        return;
      }

      this.intro.hidden = true;
      this.quiz.hidden = true;
      this.result.hidden = false;
      this.clearGlobalStatus();
      if (this.isPopup && !this.popupIsOpen) this.openPopup({ focus: false });
      const title = this.querySelector('[data-bsf-result-title]');
      const description = this.querySelector('[data-bsf-result-description]');
      const intensityChip = this.querySelector('[data-bsf-result-intensity]');
      const occasionChip = this.querySelector('[data-bsf-result-occasion]');
      const secondaryChip = this.querySelector('[data-bsf-result-secondary]');

      title.textContent = primary.title;
      description.textContent = [
        primary.description,
        intensity.description,
        occasion.description,
        secondary ? `A touch of ${secondary.title} gives the result a second point of view.` : '',
      ]
        .filter(Boolean)
        .join(' ');
      intensityChip.textContent = `Impact: ${intensity.label}`;
      occasionChip.textContent = `For: ${occasion.label}`;
      secondaryChip.hidden = !secondary;
      secondaryChip.textContent = secondary ? `Also: ${secondary.title}` : '';

      const colors = primary.colors.slice();
      const patterns = primary.patterns.slice();
      const items = primary.items.slice();
      if (secondary) {
        colors[colors.length - 1] = secondary.colors[0];
        patterns[patterns.length - 1] = secondary.patterns[0];
        items[items.length - 1] = secondary.items[0];
      }

      this.renderColors(colors);
      this.renderPatterns(patterns);
      this.renderItems(items);
      this.renderResultImages(primary);
      this.updateCollectionLinks(primary, secondary);
      this.updateEmailTags(primary, secondary, intensity, occasion);
      this.scrollDialogToTop();

      this.dispatchEvent(
        new CustomEvent('budetz:style-finder:result', {
          bubbles: true,
          detail: {
            primary: this.state.images[0],
            secondary: this.state.images[1] || null,
            intensity: this.state.intensity,
            occasion: this.state.occasion,
          },
        })
      );

      if (focus) window.requestAnimationFrame(() => title.focus());
    }

    renderResultImages(profile) {
      if (!this.resultGallery || !this.resultGalleryTrack) return;

      const images = Array.isArray(profile.resultImages) ? profile.resultImages.filter((image) => image?.src) : [];
      this.resultGalleryTrack.replaceChildren();
      this.resultGallery.hidden = images.length === 0;

      images.forEach((image, index) => {
        const card = document.createElement('figure');
        const picture = document.createElement('img');
        const caption = document.createElement('figcaption');

        card.className = 'budetz-style-result__gallery-card';
        picture.className = 'budetz-style-result__gallery-image';
        picture.src = image.src;
        picture.alt = image.alt || `${profile.title} outfit recommendation ${index + 1}`;
        picture.loading = index === 0 ? 'eager' : 'lazy';
        picture.decoding = 'async';
        if (image.width) picture.width = image.width;
        if (image.height) picture.height = image.height;

        caption.className = 'budetz-style-result__gallery-caption';
        caption.textContent = image.caption || `Look ${String(index + 1).padStart(2, '0')}`;
        card.append(picture, caption);
        this.resultGalleryTrack.appendChild(card);
      });
    }

    renderColors(colors) {
      const list = this.querySelector('[data-bsf-result-colors]');
      list.replaceChildren();
      colors.forEach((color) => {
        const item = document.createElement('li');
        item.className = 'budetz-style-result__swatch';
        item.style.setProperty('--bsf-swatch-color', color.hex);
        item.textContent = color.name;
        list.appendChild(item);
      });
    }

    renderPatterns(patterns) {
      const list = this.querySelector('[data-bsf-result-patterns]');
      list.replaceChildren();
      patterns.forEach((pattern) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const tag = document.createElement('small');
        name.textContent = pattern.name;
        tag.textContent = pattern.tag;
        item.append(name, tag);
        list.appendChild(item);
      });
    }

    renderItems(items) {
      const list = this.querySelector('[data-bsf-result-items]');
      list.replaceChildren();
      items.forEach((itemName) => {
        const item = document.createElement('li');
        item.textContent = itemName;
        list.appendChild(item);
      });
    }

    updateCollectionLinks(primary, secondary) {
      const links = {
        easy: primary.collections.easy,
        best: primary.collections.best,
        try: secondary ? secondary.collections.try : primary.collections.try,
      };

      Object.entries(links).forEach(([group, url]) => {
        const link = this.querySelector(`[data-bsf-collection-link="${group}"]`);
        if (!link || typeof url !== 'string') return;
        link.href = url;
        const groupName = link.querySelector('strong')?.textContent || 'recommendations';
        const profileName = group === 'try' && secondary ? secondary.title : primary.title;
        link.setAttribute('aria-label', `${groupName}: shop ${profileName}`);
      });

      const tryCopy = this.querySelector('[data-bsf-try-copy]');
      tryCopy.textContent = secondary
        ? `Explore ${secondary.title} as a controlled second direction.`
        : `Push ${primary.title} one step beyond your usual choice.`;
    }

    updateEmailTags(primary, secondary, intensity, occasion) {
      if (!this.emailTags) return;
      const tags = ['budetz-style-finder', primary.tag];
      if (secondary) tags.push(secondary.tag);
      tags.push(intensity.tag, occasion.tag);
      this.emailTags.value = tags.join(',');
      this.emailTags.disabled = false;
    }

    handleEmailSubmit(event) {
      if (!this.emailTags || this.emailTags.disabled || !this.answersAreComplete()) {
        event.preventDefault();
        this.showEmailClientError('Please complete the Style Image Finder before saving your profile.');
        return;
      }

      if (!this.emailConsent?.checked) {
        event.preventDefault();
        this.showEmailClientError('Please agree to receive marketing emails before saving your profile.');
        this.emailConsent?.focus();
        return;
      }

      const emailInput = this.emailForm.querySelector('input[type="email"]');
      if (!emailInput?.checkValidity()) {
        event.preventDefault();
        emailInput?.reportValidity();
        return;
      }

      this.saveStateForEmailPost();
      this.hideEmailClientError();
      const submitButton = this.emailForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Saving…';
      }
    }

    showEmailClientError(message) {
      if (!this.emailClientError) return;
      this.emailClientError.textContent = message;
      this.emailClientError.hidden = false;
    }

    hideEmailClientError() {
      if (!this.emailClientError) return;
      this.emailClientError.textContent = '';
      this.emailClientError.hidden = true;
    }

    answersAreComplete() {
      return (
        this.state.images.length > 0 &&
        this.state.images.length <= 2 &&
        Boolean(this.config.intensities[this.state.intensity]) &&
        Boolean(this.config.occasions[this.state.occasion])
      );
    }

    saveStateForEmailPost() {
      try {
        window.sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            images: this.state.images,
            intensity: this.state.intensity,
            occasion: this.state.occasion,
          })
        );
      } catch (error) {
        // The native Shopify form can still submit if browser storage is unavailable.
      }
    }

    restoreAfterEmailPost() {
      if (!this.emailSuccess && !this.emailServerError) return false;

      let savedState = null;
      try {
        savedState = JSON.parse(window.sessionStorage.getItem(this.storageKey));
      } catch (error) {
        savedState = null;
      }

      if (this.savedStateIsValid(savedState)) {
        this.state.images = savedState.images.slice(0, 2);
        this.state.intensity = savedState.intensity;
        this.state.occasion = savedState.occasion;
        this.restoreInputsFromState();
        this.showResult({ focus: false });

        const message = this.emailSuccess || this.emailServerError;
        window.requestAnimationFrame(() => {
          if (message.hasAttribute('tabindex')) message.focus();
          else this.querySelector('input[type="email"]')?.focus();
        });

        if (this.emailSuccess) {
          try {
            window.sessionStorage.removeItem(this.storageKey);
          } catch (error) {
            // No cleanup is required when browser storage is unavailable.
          }
        }
        return true;
      }

      const message = this.emailSuccess
        ? 'Your style image has been saved. If required, check your inbox to confirm your subscription.'
        : 'Shopify could not save your email. Please take the finder again and retry.';
      this.showIntro(false);
      this.showGlobalStatus(message, Boolean(this.emailServerError));
      if (this.isPopup) this.openPopup();
      return true;
    }

    savedStateIsValid(savedState) {
      if (!savedState || !Array.isArray(savedState.images)) return false;
      const imagesAreValid =
        savedState.images.length > 0 &&
        savedState.images.length <= 2 &&
        savedState.images.every((image) => Boolean(this.config.profiles[image]));
      return (
        imagesAreValid &&
        Boolean(this.config.intensities[savedState.intensity]) &&
        Boolean(this.config.occasions[savedState.occasion])
      );
    }

    restoreInputsFromState() {
      this.querySelectorAll('.budetz-style-option__input').forEach((input) => {
        const question = input.closest('[data-bsf-question]')?.dataset.bsfQuestion;
        if (question === 'image') input.checked = this.state.images.includes(input.value);
        if (question === 'intensity') input.checked = this.state.intensity === input.value;
        if (question === 'occasion') input.checked = this.state.occasion === input.value;
      });
      this.updateImageCount();
    }

    restart() {
      this.state = { step: 0, images: [], intensity: '', occasion: '' };
      this.querySelectorAll('.budetz-style-option__input').forEach((input) => {
        input.checked = false;
      });
      this.questions.forEach((question) => this.clearQuestionError(question));
      this.updateImageCount();
      this.hideEmailClientError();
      if (this.emailTags) this.emailTags.disabled = true;
      if (this.emailSuccess) this.emailSuccess.hidden = true;
      if (this.emailServerError) this.emailServerError.hidden = true;
      if (this.emailFields) this.emailFields.hidden = false;
      if (this.emailConsent) this.emailConsent.checked = false;
      const emailInput = this.emailForm?.querySelector('input[type="email"]');
      if (emailInput) emailInput.value = '';
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // No cleanup is required when browser storage is unavailable.
      }
      this.showQuestion(0);
    }

    scheduleAutoOpen() {
      if (!this.isPopup || this.dataset.autoOpen !== 'true' || this.popupWasDismissed()) return;
      if (window.Shopify?.designMode) return;

      const delay = Number(this.dataset.autoOpenDelay) || 4000;
      this.autoOpenTimer = window.setTimeout(() => {
        if (!this.popupIsOpen && !this.popupWasDismissed()) this.openPopup();
      }, Math.max(0, delay));
    }

    openPopup({ focus = true } = {}) {
      if (!this.isPopup || !this.popup || !this.dialog) return;
      if (this.autoOpenTimer) {
        window.clearTimeout(this.autoOpenTimer);
        this.autoOpenTimer = null;
      }

      if (!this.popupIsOpen) this.lastFocusedElement = document.activeElement;
      this.popup.hidden = false;
      this.popupIsOpen = true;
      this.popupOpenButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('budetz-style-finder-popup-lock');
      document.body.classList.add('budetz-style-finder-popup-lock');

      if (focus) {
        window.requestAnimationFrame(() => {
          const target = this.quiz.hidden && this.result.hidden ? this.startButton : this.dialog;
          target?.focus();
        });
      }
    }

    closePopup({ dismiss = false, restoreFocus = true } = {}) {
      if (!this.isPopup || !this.popup || !this.popupIsOpen) return;
      this.popup.hidden = true;
      this.popupIsOpen = false;
      this.popupOpenButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('budetz-style-finder-popup-lock');
      document.body.classList.remove('budetz-style-finder-popup-lock');
      if (dismiss) this.dismissAutoPopup();

      if (restoreFocus) {
        const focusTarget =
          this.lastFocusedElement &&
          this.lastFocusedElement !== document.body &&
          typeof this.lastFocusedElement.focus === 'function'
            ? this.lastFocusedElement
            : this.popupOpenButton;
        window.requestAnimationFrame(() => focusTarget?.focus());
      }
    }

    handleDocumentKeydown(event) {
      if (!this.popupIsOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePopup();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.dialog.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-bsf-open-style-finder]');
      if (!trigger || !this.isPopup) return;
      event.preventDefault();
      this.openPopup();
    }

    getFocusableElements() {
      if (!this.dialog) return [];
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      return Array.from(this.dialog.querySelectorAll(selector)).filter(
        (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null
      );
    }

    popupWasDismissed() {
      try {
        const dismissedUntil = Number(window.localStorage.getItem(this.dismissStorageKey) || 0);
        if (!dismissedUntil) return false;
        if (Date.now() < dismissedUntil) return true;
        window.localStorage.removeItem(this.dismissStorageKey);
        return false;
      } catch (error) {
        return false;
      }
    }

    dismissAutoPopup() {
      if (!this.isPopup) return;
      const days = Math.max(1, Number(this.dataset.dismissDays) || 7);
      try {
        window.localStorage.setItem(this.dismissStorageKey, String(Date.now() + days * 24 * 60 * 60 * 1000));
      } catch (error) {
        // The floating launcher remains available when browser storage is unavailable.
      }
    }

    scrollDialogToTop() {
      if (!this.isPopup || !this.dialogScroll) return;
      window.requestAnimationFrame(() => {
        this.dialogScroll.scrollTop = 0;
      });
    }

    showGlobalStatus(message, isError = false) {
      if (!this.globalStatus) return;
      this.globalStatus.textContent = message;
      this.globalStatus.hidden = false;
      this.globalStatus.style.color = isError ? '#861e1e' : '';
      this.globalStatus.style.backgroundColor = isError ? '#fff2f2' : '';
    }

    clearGlobalStatus() {
      if (!this.globalStatus) return;
      this.globalStatus.textContent = '';
      this.globalStatus.hidden = true;
      this.globalStatus.style.color = '';
      this.globalStatus.style.backgroundColor = '';
    }
  }

  customElements.define('budetz-style-finder', BudetzStyleFinder);
}
